import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";

import DatePick from "src/components/DatePick/DatePick";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import { apiService } from "src/services/api.service";

import { LoadingState } from "./MetricsCards";

import GrowthPanel, { IGrowthData } from "./GrowthPanel";
import FinancialsPanel, { IFinancialsData } from "./FinancialsPanel";
import ClubsPanel, { IClubsData } from "./ClubsPanel";
import {
  CURRENCY_OPTIONS,
  Currency,
  IMetricsFilters,
  PAYMENTS_OPTIONS,
  Payments,
  RANGE_OPTIONS,
  RangePreset,
  resolveRange,
} from "./metrics.utils";

import styles from "./CompanyMetricsTab.module.scss";

interface IProps {
  currentTab: number;
  tabIndex: number;
}

type Section = "growth" | "financials" | "clubs";

const SECTIONS: Section[] = ["growth", "financials", "clubs"];

const TABS: ITab[] = [
  { name: "Growth", actions: [] },
  { name: "Financials", actions: [] },
  { name: "Clubs", actions: [] },
];

const CompanyMetricsTab: React.FC<IProps> = ({ currentTab, tabIndex }) => {
  const { activeTab, setTab } = useTabContent();
  const [filters, setFilters] = useState<IMetricsFilters>({
    preset: "last_12_months",
    customFrom: moment().subtract(1, "year").format("YYYY-MM-DD"),
    customTo: moment().format("YYYY-MM-DD"),
    currency: "cad",
    payments: "online",
  });
  const [data, setData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const section = SECTIONS[activeTab];
  const range = useMemo(() => resolveRange(filters), [filters]);
  const params = useMemo(
    () => ({ from: range.from, to: range.to, currency: filters.currency, payments: filters.payments }),
    [range, filters.currency, filters.payments]
  );
  const cacheKey = `${section}|${JSON.stringify(params)}`;

  useEffect(() => {
    if (currentTab !== tabIndex || data[cacheKey]) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    apiService.getCompanyMetrics(section, params)
      .then((result) => {
        if (!cancelled) setData((current) => ({ ...current, [cacheKey]: result }));
      })
      .catch((e) => {
        console.error("Failed to load company metrics:", e);
        if (!cancelled) setError("Couldn't load metrics. Try again in a moment.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentTab, tabIndex, cacheKey]);

  if (currentTab !== tabIndex) {
    return null;
  }

  const update = (patch: Partial<IMetricsFilters>) => setFilters((current) => ({ ...current, ...patch }));
  const option = <T extends string>(options: { id: T; value: string }[], id: T): IOption => options.find((o) => o.id === id) as IOption;
  const current = data[cacheKey];

  const renderSection = (s: Section) => {
    if (s !== section) return <div />;
    if (error) return <div className={styles.empty}>{error}</div>;
    if (!current) return loading ? <LoadingState /> : <div />;
    if (s === "growth") return <GrowthPanel data={current as IGrowthData} />;
    if (s === "financials") return <FinancialsPanel data={current as IFinancialsData} currency={filters.currency} />;
    return <ClubsPanel data={current as IClubsData} currency={filters.currency} />;
  };

  return (
    <div className={styles.CompanyMetricsTab}>
      <div className={styles.filters}>
        <div className={styles.filter}>
          <Dropdown
            label="Date range"
            placeholder=""
            options={RANGE_OPTIONS}
            value={option(RANGE_OPTIONS, filters.preset)}
            onChange={(_e: unknown, value: IOption) => update({ preset: value.id as RangePreset })}
          />
        </div>
        {filters.preset === "custom" && (
          <>
            <DatePick
              className={styles.filter}
              label="From"
              value={moment(filters.customFrom).toDate()}
              maxDate={moment(filters.customTo).toDate()}
              onChange={(date: Date | null) => date && update({ customFrom: moment(date).format("YYYY-MM-DD") })}
            />
            <DatePick
              className={styles.filter}
              label="To"
              value={moment(filters.customTo).toDate()}
              minDate={moment(filters.customFrom).toDate()}
              onChange={(date: Date | null) => date && update({ customTo: moment(date).format("YYYY-MM-DD") })}
            />
          </>
        )}
        {section !== "growth" && (
          <>
            <div className={styles.filter}>
              <Dropdown
                label="Currency"
                placeholder=""
                options={CURRENCY_OPTIONS}
                value={option(CURRENCY_OPTIONS, filters.currency)}
                onChange={(_e: unknown, value: IOption) => update({ currency: value.id as Currency })}
              />
            </div>
            <div className={styles.filter}>
              <Dropdown
                label="Payments"
                placeholder=""
                options={PAYMENTS_OPTIONS}
                value={option(PAYMENTS_OPTIONS, filters.payments)}
                onChange={(_e: unknown, value: IOption) => update({ payments: value.id as Payments })}
              />
            </div>
          </>
        )}
        {loading && current !== undefined && <span className={styles.refreshing}>Refreshing…</span>}
      </div>

      <TabContent setTab={setTab} activeTab={activeTab} tabs={TABS}>
        {renderSection("growth")}
        {renderSection("financials")}
        {renderSection("clubs")}
      </TabContent>
    </div>
  );
};

export default CompanyMetricsTab;
